<!--
@component
A single toast notification.

@internal Rendered by the toast container. Show toasts with the helpers in `$lib/toast`.
-->
<script lang="ts">
  import Info from "@lucide/svelte/icons/info";
  import PartyPopper from "@lucide/svelte/icons/party-popper";
  import AlertTriangle from "@lucide/svelte/icons/triangle-alert";
  import XIcon from "@lucide/svelte/icons/x";
  import { toast } from "svelte-sonner";

  import Avatar from "$lib/design-systems/product/primitives/avatar/Avatar.svelte";
  import type { Toast } from "$lib/design-systems/product/primitives/feedback/toast";
  import A from "$lib/design-systems/product/primitives/typography/A.svelte";

  type Props = {
    id: string;
  } & Toast;

  let props: Props = $props();
  let { id, kind, message, link } = $derived(props);
</script>

<!-- keep in sync with clickOutside in Drawer.svelte -->
<div class="toast-box">
  <div
    class="flex min-w-0 {kind === 'activity' ? 'items-start' : 'items-center'}"
  >
    <div class="mr-3 shrink-0">
      {#if kind === "info"}
        <div class="rounded-full bg-informational-bold p-1.5">
          <Info size={20} class="icon-informational-bold" />
        </div>
      {:else if kind === "success"}
        <div class="rounded-full bg-success-bold p-1.5">
          <PartyPopper size={20} class="icon-success-bold" />
        </div>
      {:else if kind === "error"}
        <div class="rounded-full bg-danger-bold p-1.5">
          <AlertTriangle size={20} class="icon-danger-bold" />
        </div>
      {/if}
      {#if props.kind === "activity"}
        <Avatar
          avatarUrl={props.user.avatarUrl}
          name={props.user.name}
          size={32}
          disableBorder
        />
      {/if}
    </div>
    <p class="min-w-0 wrap-break-word text-primary">
      {message}
      {#if link}
        <span class="inline-block whitespace-nowrap"
          ><A href={link.href}>{link.name}</A></span
        >
      {/if}
    </p>
  </div>

  <button
    class="border-primary p-2 icon-secondary transition-colors hover:border-primary-hover hover:icon-primary-active"
    onclick={() => toast.dismiss(id)}
  >
    <XIcon size={20} />
  </button>
</div>

<style lang="postcss">
  @reference "$app.css";

  .toast-box {
    @apply w-[380px] border border-secondary bg-elevated backdrop-blur-xs;
    @apply pointer-events-auto flex items-center justify-between rounded-lg px-3 py-3;
    @apply text-sm;
  }
</style>
